// portal-session — the widget's line to the customer-portal edge function
// (the SAME backend the Phyto storefront portal and the AI agent's subscription
// tools converge on). One JWT session per logged-in Shopify customer powers
// BOTH engines: FlowIQ subscriptions (native routes) and Loop (proxied
// /loop/* routes, contact always taken from the session server-side).
//
// Identity: every Shopify storefront exposes the logged-in customer's id via
// `window.__st.cid` / `ShopifyAnalytics.meta.page.customerId`, and the shop's
// permanent domain via `window.Shopify.shop` — so the widget needs NO theme or
// app-embed changes to know who is signed in. The id is only a claim: the
// portal /session endpoint verifies the customer exists on the org's own
// Shopify before minting anything (the same trust model as the storefront
// portal, which also rides the logged-in page's customer id).

const PORTAL_API =
  "https://zvhwjpeeapujvuudfdps.supabase.co/functions/v1/customer-portal";

export function detectShopifyCustomerId(): string | null {
  try {
    const w = window as any;
    const cid =
      w.__st?.cid ??
      w.ShopifyAnalytics?.meta?.page?.customerId ??
      w.meta?.page?.customerId ??
      null;
    if (cid) return String(cid);
    // Embed-supplied override (script tag data attribute) for exotic setups.
    const script = document.querySelector("script[data-shopify-customer-id]");
    const attr = script?.getAttribute("data-shopify-customer-id");
    return attr && attr.trim() ? attr.trim() : null;
  } catch {
    return null;
  }
}

export function detectShopDomain(): string | null {
  try {
    const w = window as any;
    if (w.Shopify?.shop) return String(w.Shopify.shop);
    const script = document.querySelector("script[data-shop-domain]");
    const attr = script?.getAttribute("data-shop-domain");
    return attr && attr.trim() ? attr.trim() : null;
  } catch {
    return null;
  }
}

interface SessionState {
  token: string;
  mintedAt: number;
}

let session: SessionState | null = null;
let mintPromise: Promise<string> | null = null;

async function mint(shopDomain: string, customerId: string): Promise<string> {
  const res = await fetch(`${PORTAL_API}/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      shop_domain: shopDomain,
      shopify_customer_id: customerId,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.token) {
    throw new Error(data?.error || `Could not start a session (HTTP ${res.status})`);
  }
  session = { token: data.token, mintedAt: Date.now() };
  return data.token;
}

async function getToken(shopDomain: string, customerId: string): Promise<string> {
  // Tokens live 15 min server-side; refresh proactively at 12.
  if (session && Date.now() - session.mintedAt < 12 * 60 * 1000) return session.token;
  if (!mintPromise) {
    mintPromise = mint(shopDomain, customerId).finally(() => { mintPromise = null; });
  }
  return mintPromise;
}

export function resetPortalSession(): void {
  session = null;
  adopted = null;
  try { sessionStorage.removeItem(OTP_STORE_KEY); } catch { /* private mode */ }
}

// ── OTP sessions (flag widget_manage_no_login) ───────────────────────────────
// The no-store-login path: a one-time code proves channel possession, the
// portal mints a 60-minute contact-scoped token. No silent re-mint is possible
// (that would defeat the proof) — expiry surfaces as REVERIFY to the UI.

const OTP_STORE_KEY = "flowiq-manage-otp-session";

interface AdoptedToken { token: string; expiresAt: number }
let adopted: AdoptedToken | null = null;
try {
  const raw = sessionStorage.getItem(OTP_STORE_KEY);
  if (raw) {
    const parsed = JSON.parse(raw) as AdoptedToken;
    if (parsed?.token && parsed.expiresAt > Date.now() + 30_000) adopted = parsed;
  }
} catch { /* private mode */ }

export function hasOtpSession(): boolean {
  return !!adopted && adopted.expiresAt > Date.now() + 30_000;
}

export async function otpStart(organizationId: string, ident: { phone?: string; email?: string }): Promise<{ sent: boolean; channel: string | null; sent_to: string | null; otp_ref: string | null }> {
  const res = await fetch(`${PORTAL_API}/widget-otp/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ organization_id: organizationId, phone: ident.phone, email: ident.email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "Could not send a code");
  return { sent: !!data.sent, channel: data.channel ?? null, sent_to: data.sent_to ?? null, otp_ref: data.otp_ref ?? null };
}

export async function otpVerify(otpRef: string, code: string): Promise<{ verified: boolean; reason?: string; attempts_left?: number }> {
  const res = await fetch(`${PORTAL_API}/widget-otp/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ otp_ref: otpRef, code }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "Verification failed");
  if (data.verified && data.token) {
    adopted = { token: data.token, expiresAt: data.expires_at ? new Date(data.expires_at).getTime() : Date.now() + 55 * 60_000 };
    try { sessionStorage.setItem(OTP_STORE_KEY, JSON.stringify(adopted)); } catch { /* private mode */ }
  }
  return { verified: !!data.verified, reason: data.reason, attempts_left: data.attempts_left };
}

export const REVERIFY = "REVERIFY";

// ── Admin "view as customer" (widget twin of the portal admin bar) ──────────
// Server-verified: allowlisted admin identity on the CURRENT session + the
// portal's access code + the org's widget_manage_no_login flag. While an
// impersonation token is set, every portalFetch rides it; Exit drops it and
// falls straight back to the admin's own session.

const IMP_STORE_KEY = "flowiq-manage-imp-session";
interface ImpersonationState { token: string; expiresAt: number; label: string }
let impersonation: ImpersonationState | null = null;
try {
  const raw = sessionStorage.getItem(IMP_STORE_KEY);
  if (raw) {
    const parsed = JSON.parse(raw) as ImpersonationState;
    if (parsed?.token && parsed.expiresAt > Date.now() + 30_000) impersonation = parsed;
  }
} catch { /* private mode */ }

export function getImpersonation(): { label: string } | null {
  return impersonation && impersonation.expiresAt > Date.now() + 30_000 ? { label: impersonation.label } : null;
}

export function exitImpersonation(): void {
  impersonation = null;
  try { sessionStorage.removeItem(IMP_STORE_KEY); } catch { /* private mode */ }
}

export async function startImpersonation(
  shopDomain: string | null,
  customerId: string | null,
  adminCode: string,
  ident: { email?: string; phone?: string },
): Promise<{ label: string }> {
  // Always authenticate the request with the admin's OWN session, never a
  // previous impersonation token.
  const canMint = !!(shopDomain && customerId);
  const ownToken = canMint ? await getToken(shopDomain!, customerId!) : adopted?.token;
  if (!ownToken) throw new Error(REVERIFY);
  const res = await fetch(`${PORTAL_API}/widget-admin/impersonate`, {
    method: "POST",
    headers: { Authorization: `Bearer ${ownToken}`, "Content-Type": "application/json" },
    body: JSON.stringify({ admin_code: adminCode, email: ident.email, phone: ident.phone }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.token) throw new Error(data?.error || "Could not open that customer's view");
  const label = data.target?.name || data.target?.email || data.target?.whatsapp_id || "customer";
  impersonation = {
    token: data.token,
    expiresAt: data.expires_at ? new Date(data.expires_at).getTime() : Date.now() + 55 * 60_000,
    label,
  };
  try { sessionStorage.setItem(IMP_STORE_KEY, JSON.stringify(impersonation)); } catch { /* private mode */ }
  return { label };
}

/** Authenticated portal call. Store-login sessions silently re-mint on 401
 *  (we hold the customer id); OTP sessions cannot — their 401 throws REVERIFY
 *  so the UI can ask for a fresh code. Store-login wins when both exist. */
export async function portalFetch(
  shopDomain: string | null,
  customerId: string | null,
  path: string,
  init?: { method?: string; body?: unknown },
): Promise<any> {
  const doCall = async (token: string) =>
    fetch(`${PORTAL_API}${path}`, {
      method: init?.method ?? "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: init?.method === "GET" ? undefined : JSON.stringify(init?.body ?? {}),
    });

  const canMint = !!(shopDomain && customerId);
  // An active admin impersonation outranks the viewer's own session.
  if (impersonation && impersonation.expiresAt > Date.now() + 5_000) {
    const res0 = await doCall(impersonation.token);
    if (res0.status === 401) {
      exitImpersonation();
      throw new Error(REVERIFY);
    }
    const data0 = await res0.json().catch(() => ({}));
    if (!res0.ok) throw new Error(data0?.error || `Request failed (HTTP ${res0.status})`);
    if (data0 && data0.success === false) throw new Error(data0.error || "Request failed");
    return data0;
  }
  let res = await doCall(canMint ? await getToken(shopDomain!, customerId!) : (adopted?.token ?? ""));
  if (res.status === 401) {
    if (canMint) {
      session = null;
      res = await doCall(await getToken(shopDomain!, customerId!));
    } else {
      adopted = null;
      try { sessionStorage.removeItem(OTP_STORE_KEY); } catch { /* private mode */ }
      throw new Error(REVERIFY);
    }
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed (HTTP ${res.status})`);
  if (data && data.success === false) throw new Error(data.error || "Request failed");
  return data;
}
